import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import { Link } from 'react-router-dom';
import { SEOHead, organizationSchema, mobileAppSchema, BRAND } from './SEOUtils';

export default function EthicalNonMonogamyDatingPage() {
  const schemas = [organizationSchema, mobileAppSchema];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Ethical Non-Monogamy Dating App | ENM Relationships | UnityLove"
        description="UnityLove is the ENM dating app for polyamory, polygamy, open relationships—and welcoming to intentional monogamy. Black poly-centered, consent-focused."
        canonical="/ethical-non-monogamy-dating"
        schemas={schemas}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Ethical Non-Monogamy Dating for Modern Relationships
          </h1>
          <p className="text-xl text-slate-300 mb-4 max-w-2xl mx-auto">
            UnityLove is the ENM dating app where practitioners of polyamory, polygamy, and open relationships find partners who understand consensual, transparent connection.
          </p>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Black poly-centered—and welcoming to intentional monogamy. Whatever structure works for you, we prioritize honesty, consent, and respect.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
            >
              Join UnityLove Free
            </Link>
            <a
              href={BRAND.playStoreUrl}
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-colors"
            >
              Download the App
            </a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-16">
        
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            What Is Ethical Non-Monogamy?
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            Ethical non-monogamy (ENM) is an umbrella term for relationship styles that involve more than two people, with the knowledge and consent of everyone involved. The key word is "ethical"—these aren't affairs or deception, but transparent, communicative relationships where all parties are informed and consenting.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            ENM includes many relationship styles: polyamory, open relationships, relationship anarchy, swinging, and more. What unites them is a commitment to honesty, consent, and respecting everyone's boundaries and autonomy.
          </p>
          <p className="text-lg text-slate-700">
            UnityLove was built for ENM practitioners who are tired of explaining their relationship style on apps designed for monogamy. Here, ethical non-monogamy isn't a niche—it's the norm.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Types of Ethical Non-Monogamy We Support
          </h2>
          <div className="space-y-6">
            <div className="border-l-4 border-rose-600 pl-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Polyamory</h3>
              <p className="text-slate-700">Multiple romantic and/or sexual relationships simultaneously, with the knowledge of all involved. May be hierarchical or non-hierarchical. <Link to="/poly-dating-app" className="text-rose-600 hover:underline">Explore our poly dating features →</Link></p>
            </div>
            <div className="border-l-4 border-rose-600 pl-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Polygamy</h3>
              <p className="text-slate-700">Structured multi-spouse family commitment, often rooted in cultural or spiritual traditions. <Link to="/black-polygamy-dating" className="text-rose-600 hover:underline">Explore Black polygamy dating →</Link></p>
            </div>
            <div className="border-l-4 border-rose-600 pl-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Open Relationships</h3>
              <p className="text-slate-700">A primary couple who may engage in sexual or romantic connections outside the relationship, often with specific boundaries and agreements.</p>
            </div>
            <div className="border-l-4 border-rose-600 pl-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Relationship Anarchy</h3>
              <p className="text-slate-700">Rejecting hierarchies and predefined relationship rules—each relationship is defined by the people in it, not by social norms.</p>
            </div>
            <div className="border-l-4 border-rose-600 pl-6">
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Intentional Monogamy</h3>
              <p className="text-slate-700">Traditional commitment practiced with clarity, communication, and purpose—welcomed in our community alongside non-monogamous structures.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            ENM Dating Principles on UnityLove
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            Every feature on UnityLove is designed with ENM principles in mind:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-slate-50 rounded-xl">
              <div className="text-4xl mb-3">🤝</div>
              <h3 className="font-semibold text-slate-900 mb-2">Consent</h3>
              <p className="text-slate-700 text-sm">All interactions require clear, enthusiastic consent. Features support boundary-setting and checking in.</p>
            </div>
            <div className="text-center p-6 bg-slate-50 rounded-xl">
              <div className="text-4xl mb-3">💬</div>
              <h3 className="font-semibold text-slate-900 mb-2">Communication</h3>
              <p className="text-slate-700 text-sm">Tools for honest conversation about needs, boundaries, and expectations from the start.</p>
            </div>
            <div className="text-center p-6 bg-slate-50 rounded-xl">
              <div className="text-4xl mb-3">👁️</div>
              <h3 className="font-semibold text-slate-900 mb-2">Transparency</h3>
              <p className="text-slate-700 text-sm">Profile options to share your relationship structure, existing partners, and what everyone knows.</p>
            </div>
          </div>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Black Ethical Non-Monogamy
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            ENM in Black communities has its own history, challenges, and joys. From ancestral African relationship practices to modern-day Black poly communities, non-monogamy has always been part of our story—even when mainstream narratives ignore it.
          </p>
          <p className="text-lg text-slate-700 mb-4">
            UnityLove centers Black ENM practitioners with features and community designed for cultural understanding. No more explaining code-switching or navigating both racial and relationship marginalization.
          </p>
          <p className="text-lg text-slate-700">
            Explore our <Link to="/black-poly-dating" className="text-rose-600 hover:text-rose-700 underline">Black poly dating</Link> community for culturally-centered connection.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">
            Getting Started with ENM Dating
          </h2>
          <p className="text-lg text-slate-700 mb-4">
            New to ethical non-monogamy dating? Here's how UnityLove helps you navigate:
          </p>
          <ol className="list-decimal list-inside space-y-3 text-lg text-slate-700">
            <li><strong>Define your style:</strong> Use our profile options to articulate what ENM means to you—no pressure to fit a label.</li>
            <li><strong>Set clear intentions:</strong> Tell potential matches what you're seeking, from casual connections to serious partners.</li>
            <li><strong>Communicate openly:</strong> Use our conversation tools to discuss boundaries, expectations, and logistics before meeting.</li>
            <li><strong>Join the community:</strong> Participate in discussions, events, and speed dating nights designed for intentional connection.</li>
          </ol>
        </section>

        {/* CTA Section */}
        <section className="bg-slate-900 text-white rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-bold mb-4">Find Your ENM Community</h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Join a community focused on intentional, consent-based relationships on UnityLove.
          </p>
          <Link
            to="/auth?mode=signup"
            className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
          >
            Create Your Profile Free
          </Link>
        </section>

        {/* Choose Your Structure Section */}
        <section className="mb-12 bg-rose-50 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
            Choose the Relationship Structure That Fits Your Values
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polyamory</h3>
              <p className="text-slate-600 text-sm">Multiple loving relationships practiced with honesty, consent, and open communication.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Polygamy</h3>
              <p className="text-slate-600 text-sm">Structured multi-partner or multi-spouse commitment, often rooted in family, culture, and long-term responsibility.</p>
            </div>
            <div className="bg-white p-6 rounded-xl text-center">
              <h3 className="font-semibold text-slate-900 mb-2">Intentional Monogamy</h3>
              <p className="text-slate-600 text-sm">Traditional one-partner commitment built on clarity, trust, and shared purpose.</p>
            </div>
          </div>
          <p className="text-center text-slate-700 mt-6">
            Whatever your structure, UnityLove prioritizes honesty, consent, and respect.
          </p>
        </section>

        {/* Related Pages */}
        <section className="mt-12 pt-8 border-t border-slate-200">
          <h3 className="text-xl font-semibold text-slate-900 mb-4">Explore More</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/black-poly-dating" className="text-rose-600 hover:text-rose-700 underline">Black Poly Dating</Link>
            <Link to="/poly-dating-app" className="text-rose-600 hover:text-rose-700 underline">Poly Dating App</Link>
            <Link to="/black-polyamory-dating-app" className="text-rose-600 hover:text-rose-700 underline">Black Polyamory Dating</Link>
            <Link to="/about-unitylove" className="text-rose-600 hover:text-rose-700 underline">About UnityLove</Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-100 py-8 px-4">
        <div className="max-w-4xl mx-auto text-center text-slate-600">
          <p>© {new Date().getFullYear()} UnityLove. Ethical non-monogamy dating for conscious connections.</p>
        </div>
      </footer>
    </div>
  );
}
